import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

import { isAuthenticated, login } from "@/shared/api";
import { isInsideTelegram } from "@/shared/telegram";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";

type Status = "checking" | "ready" | "failed";

/**
 * Вход выполняется до первого запроса данных: все ручки, кроме `/legal/{slug}`
 * и `/auth/telegram`, требуют Bearer-токен, и без него главная получила бы 401
 * ещё до отрисовки.
 *
 * Провал входа означает одно из двух: приложение открыто вне Telegram (и в dev
 * не задан `VITE_TG_INIT_DATA`) либо пользователь заблокирован. В обоих случаях
 * показывается экран с объяснением, а не пустота.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>(() => (isAuthenticated() ? "ready" : "checking"));

  useEffect(() => {
    if (status !== "checking") return;

    let cancelled = false;
    void login().then((ok) => {
      if (!cancelled) setStatus(ok ? "ready" : "failed");
    });

    return () => {
      cancelled = true;
    };
  }, [status]);

  if (status === "checking") return <ScreenSkeleton />;

  if (status === "failed") {
    return (
      <ScreenState
        tone="warning"
        icon="warningFill"
        title={t("errors.outsideTelegram")}
        body={isInsideTelegram() ? t("errors.authFailed") : t("errors.outsideTelegramBody")}
      />
    );
  }

  // Появление приложения после входа: контент проявляется, а не возникает
  // на месте скелетона (FE-13).
  return <div className="animate-fade-in">{children}</div>;
}
